import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Spin, Switch, message, Select, Upload, Collapse, Segmented } from 'antd';
import { RobotOutlined, FileImageOutlined, FileWordOutlined, FileExcelOutlined, FilePdfOutlined, DeleteOutlined, RollbackOutlined, SendOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiService } from '../WorkflowDesigner/services/apiService';

const { TextArea } = Input;

const markdownChatStyles = {
  margin: 0,
  lineHeight: 1.5
};
const markdownComponents = (isUser) => ({
  p: ({ children }) => <p style={{ margin: '0 0 6px 0' }}>{children}</p>,
  h1: ({ children }) => <div style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px 0' }}>{children}</div>,
  h2: ({ children }) => <div style={{ fontSize: 14, fontWeight: 700, margin: '0 0 6px 0' }}>{children}</div>,
  h3: ({ children }) => <div style={{ fontSize: 13, fontWeight: 700, margin: '0 0 4px 0' }}>{children}</div>,
  ul: ({ children }) => <ul style={{ margin: '0 0 6px 0', paddingLeft: 18 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '0 0 6px 0', paddingLeft: 18 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
  code: ({ inline, children }) =>
    inline ? (
      <code style={{ background: isUser ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.08)', padding: '1px 4px', borderRadius: 4, fontSize: '0.92em' }}>{children}</code>
    ) : (
      <code style={{ display: 'block', background: isUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)', padding: '6px 8px', borderRadius: 4, fontSize: 12, overflow: 'auto', marginTop: 4 }}>{children}</code>
    ),
  strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>
});

const DOC_ACCEPT = '.doc,.docx,.xls,.xlsx,.pdf';
const DOC_EXTENSIONS = ['.doc', '.docx', '.xls', '.xlsx', '.pdf'];

/** 將 UI 對話紀錄轉成 API 用的 chatHistory（不含本次新訊息） */
const buildChatHistoryForApi = (msgs) =>
  (msgs || [])
    .map((m) => ({
      role: (m.role || m.Role || 'user').toLowerCase(),
      content: (m.content || m.Content || '').trim()
    }))
    .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content);

const EFormDesignerAIChat = ({
  eFormDefinitionId,
  htmlContent,
  onSuccess,
  isGenerating,
  setIsGenerating
}) => {
  const { t } = useLanguage();
  const messagesEndRef = useRef(null);
  const prevEFormDefinitionIdRef = useRef(eFormDefinitionId);
  const messagesRef = useRef([]);
  const [messages, setMessages] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  messagesRef.current = messages;
  const [aiPrompt, setAiPrompt] = useState('');
  const [includeCurrentHtml, setIncludeCurrentHtml] = useState(() => (htmlContent || '').trim().length > 0);
  const [aiProviders, setAiProviders] = useState([]);
  const [selectedProviderKey, setSelectedProviderKey] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [formStyle, setFormStyle] = useState('ui'); // 'ui' | 'print'
  const [sendDefaultSystemPrompt, setSendDefaultSystemPrompt] = useState(true);
  const [modifySystemPrompt, setModifySystemPrompt] = useState(false);
  const [customSystemPrompt, setCustomSystemPrompt] = useState('');
  const [loadingDefaultPrompt, setLoadingDefaultPrompt] = useState(false);

  useEffect(() => {
    loadAiProviders();
  }, []);

  // 僅在「第一次等 prompt」時預設為 on；已有表單內容（修改中）時預設為 off，避免設計走樣
  useEffect(() => {
    const hasExistingForm = (htmlContent || '').trim().length > 0;
    setSendDefaultSystemPrompt(!hasExistingForm);
  }, [htmlContent]);

  useEffect(() => {
    if (eFormDefinitionId) {
      const prevId = prevEFormDefinitionIdRef.current;
      prevEFormDefinitionIdRef.current = eFormDefinitionId;
      // 表單剛儲存：formId 從 null 變為有值，若畫面上有未寫入的對話，先送出再載入歷史
      const currentMessages = messagesRef.current;
      const pending = currentMessages.filter(m => !m.Id && (m.role || m.Role));
      if (pending.length > 0 && !prevId) {
        const token = localStorage.getItem('token');
        const items = pending.map((m, i) => ({
          EFormDefinitionId: eFormDefinitionId,
          Role: m.role || m.Role,
          Content: m.content || m.Content || '',
          AttachmentInfo: m.attachmentInfo ?? m.AttachmentInfo ?? null,
          ResultFormName: m.resultFormName ?? m.ResultFormName ?? null,
          HtmlContent: m.htmlContent ?? m.HtmlContent ?? null,
          MessageIndex: i
        }));
        fetch('/api/FormsUpload/editing-ai-chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(items)
        })
          .then(res => {
            if (res.ok) loadChatHistory();
            else setMessages(prev => prev);
          })
          .catch(() => setMessages(prev => prev))
          .finally(() => {});
        return;
      }
      loadChatHistory();
    } else {
      prevEFormDefinitionIdRef.current = null;
      setMessages([]);
    }
  }, [eFormDefinitionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 新建表單時，第一次生成或手動畫出內容後，自動將「包含當前 HTML」設為 on
  useEffect(() => {
    const hasContent = (htmlContent || '').trim().length > 0;
    if (hasContent) {
      setIncludeCurrentHtml(true);
    }
  }, [htmlContent]);

  const loadAiProviders = async () => {
    try {
      setLoadingProviders(true);
      const providers = await apiService.fetchAiProviders();
      setAiProviders(providers || []);
      setSelectedProviderKey(prev => {
        if (prev) return prev;
        if (providers?.length > 0) {
          const active = providers.find(p => p.active);
          return (active || providers[0]).providerKey;
        }
        return '';
      });
    } catch (error) {
      console.error('Failed to load AI providers', error);
      message.error(t('eformDesigner.loadAiProvidersFailed'));
    } finally {
      setLoadingProviders(false);
    }
  };

  const loadChatHistory = async () => {
    if (!eFormDefinitionId) return;
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/FormsUpload/editing-ai-chat?eFormDefinitionId=${eFormDefinitionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Failed to load chat history');
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('Load chat history failed', e);
      setMessages([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleImageUpload = (file) => {
    if (!file.type.startsWith('image/')) {
      message.error(t('eformDesigner.pleaseUploadImage') || '請上傳圖片文件');
      return false;
    }
    if (file.size / 1024 / 1024 >= 10) {
      message.error(t('eformDesigner.imageSizeLimit') || '圖片大小不能超過 10MB');
      return false;
    }
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    setUploadedImage(file);
    return false;
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
  };

  const handleDocumentUpload = (file) => {
    const ext = (file.name || '').toLowerCase().replace(/.*\./, '.');
    if (!DOC_EXTENSIONS.includes(ext)) {
      message.error(t('eformDesigner.uploadWordExcelPdfOnly') || '僅支援 Word/Excel/PDF');
      return false;
    }
    if (file.size / 1024 / 1024 >= 50) {
      message.error(t('eformDesigner.documentSizeLimit') || '文件大小不能超過 50MB');
      return false;
    }
    setUploadedDocument(file);
    return false;
  };

  const handleRemoveDocument = () => setUploadedDocument(null);

  const loadDefaultSystemPrompt = async (style = formStyle, force = false) => {
    if (!force && customSystemPrompt.trim()) return;
    setLoadingDefaultPrompt(true);
    try {
      const token = localStorage.getItem('token');
      const query = style === 'print' ? '?style=print' : '';
      const res = await fetch(`/api/FormsUpload/ai-default-system-prompt${query}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await res.json();
      if (data.defaultSystemPrompt != null) setCustomSystemPrompt(data.defaultSystemPrompt);
    } catch (e) {
      message.error(t('eformDesigner.loadDefaultPromptFailed') || '無法載入預設系統 prompt');
    } finally {
      setLoadingDefaultPrompt(false);
    }
  };

  const handleFormStyleChange = (value) => {
    setFormStyle(value);
    if (value === 'print') {
      setSendDefaultSystemPrompt(true);
    }
    if (modifySystemPrompt) {
      setCustomSystemPrompt('');
      loadDefaultSystemPrompt(value, true);
    }
  };

  const handleModifySystemPromptChange = (checked) => {
    setModifySystemPrompt(checked);
    if (checked) loadDefaultSystemPrompt(formStyle);
    else setCustomSystemPrompt('');
  };

  const handleSendDefaultSystemPromptChange = (checked) => {
    setSendDefaultSystemPrompt(checked);
    if (!checked) {
      setModifySystemPrompt(false);
      setCustomSystemPrompt('');
    }
  };

  const saveChatMessages = async (userItem, assistantItem) => {
    const token = localStorage.getItem('token');
    const res = await fetch('/api/FormsUpload/editing-ai-chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify([userItem, assistantItem])
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const errMsg = err.error || err.message || `HTTP ${res.status}`;
      console.error('[EFormDesignerAIChat] saveChatMessages failed:', res.status, errMsg, err);
      message.error(t('eformDesigner.chatSaveFailed') || '對話紀錄儲存失敗：' + errMsg);
      throw new Error(errMsg);
    }
    const created = await res.json();
    return created;
  };

  const handleSend = async () => {
    const hasPrompt = !!aiPrompt.trim();
    const hasImage = !!uploadedImage;
    const hasDocument = !!uploadedDocument;
    if (!hasPrompt && !hasImage && !hasDocument) {
      message.warning(t('eformDesigner.pleaseEnterYourRequirements') || '請輸入需求描述、上傳表單圖片或 Word/Excel/PDF 文件');
      return;
    }
    if (aiProviders.length > 0 && !selectedProviderKey) {
      message.warning(t('eformDesigner.aiProviderRequired'));
      return;
    }

    setIsGenerating(true);
    const nextIndex = messages.length;
    const chatHistory = buildChatHistoryForApi(messages);
    const attachmentInfo = {
      hasImage: !!uploadedImage,
      hasDocument: !!uploadedDocument,
      documentName: uploadedDocument?.name || null
    };

    try {
      const token = localStorage.getItem('token');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15 * 60 * 1000);
      const effectiveSendDefaultSystemPrompt = formStyle === 'print' ? true : sendDefaultSystemPrompt;

      let response;
      if (uploadedImage || uploadedDocument) {
        const formData = new FormData();
        if (uploadedImage) formData.append('file', uploadedImage);
        if (uploadedDocument) formData.append('documentFile', uploadedDocument);
        formData.append('prompt', aiPrompt.trim() || '');
        formData.append('includeCurrentHtml', includeCurrentHtml.toString());
        formData.append('providerKey', selectedProviderKey || '');
        if (includeCurrentHtml && (htmlContent || '').trim()) formData.append('currentHtml', (htmlContent || '').trim());
        formData.append('useDefaultSystemPrompt', effectiveSendDefaultSystemPrompt.toString());
        formData.append('formStyle', formStyle);
        if (effectiveSendDefaultSystemPrompt && modifySystemPrompt && customSystemPrompt.trim()) formData.append('customSystemPrompt', customSystemPrompt.trim());
        // patch 模式：有現有 HTML 時自動使用 operations 格式
        formData.append('returnFormat', (includeCurrentHtml && (htmlContent || '').trim()) ? 'operations' : 'html');
        if (chatHistory.length > 0) {
          formData.append('chatHistory', JSON.stringify(chatHistory));
        }
        response = await fetch('/api/FormsUpload/ai-generate-with-image', {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
          signal: controller.signal
        });
      } else {
        const requestData = {
          prompt: aiPrompt.trim(),
          includeCurrentHtml,
          providerKey: selectedProviderKey || null,
          UseDefaultSystemPrompt: effectiveSendDefaultSystemPrompt,
          FormStyle: formStyle,
          CustomSystemPrompt: effectiveSendDefaultSystemPrompt && modifySystemPrompt && customSystemPrompt.trim() ? customSystemPrompt.trim() : null,
          // patch 模式：有現有 HTML 時自動使用 operations 格式
          ReturnFormat: (includeCurrentHtml && (htmlContent || '').trim()) ? 'operations' : 'html',
          chatHistory: chatHistory.length > 0 ? chatHistory : undefined
        };
        if (includeCurrentHtml && (htmlContent || '').trim()) requestData.CurrentHtml = (htmlContent || '').trim();
        response = await fetch('/api/FormsUpload/ai-generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(requestData),
          signal: controller.signal
        });
      }

      clearTimeout(timeoutId);
      const result = await response.json();

      if (!result.success) {
        message.error(`❌ ${t('eformDesigner.generationFailed')}${result.error || ''}`);
        return;
      }

      // 後端已套用 patch operations（或全量生成），直接取 htmlContent
      const finalHtml = result.htmlContent;

      // 安全驗證：確保 finalHtml 是合法 HTML（防止 AI 異常回應破壞表單）
      const isValidHtml = finalHtml && (
        finalHtml.includes('<html') || finalHtml.includes('<!DOCTYPE') ||
        finalHtml.includes('<body') || finalHtml.includes('<table') || finalHtml.includes('<form')
      );
      if (!isValidHtml) {
        message.error(`❌ ${t('eformDesigner.generationFailed')}AI 返回了無效格式，表單未更新`);
        return;
      }

      if (result.mode === 'patch') {
        console.log(`[EFormDesignerAIChat] patch 模式：套用 ${result.appliedCount ?? '?'} 個，跳過 ${result.skippedCount ?? 0} 個 (後端處理)`);
      }

      message.success(t('eformDesigner.aiFormGeneratedSuccess'));
      onSuccess(finalHtml, result.formName || t('eformDesigner.aiGeneratedForm'));

      const userContent = aiPrompt.trim() || (t('eformDesigner.aiFormGeneratedSuccess') || '已根據上傳的圖片/文件生成');
      const assistantContent = (result.summary && result.summary.trim())
        ? result.summary.trim()
        : (t('eformDesigner.aiFormUpdatedSuccess') || '已根據您的要求更新表單');

      setAiPrompt('');
      setUploadedImage(null);
      setImagePreview(null);
      setUploadedDocument(null);

      const userItem = {
        EFormDefinitionId: eFormDefinitionId || '00000000-0000-0000-0000-000000000000',
        Role: 'user',
        Content: userContent,
        AttachmentInfo: JSON.stringify(attachmentInfo),
        ResultFormName: null,
        HtmlContent: null,
        MessageIndex: nextIndex
      };
      const assistantItem = {
        EFormDefinitionId: eFormDefinitionId || '00000000-0000-0000-0000-000000000000',
        Role: 'assistant',
        Content: assistantContent,
        AttachmentInfo: null,
        ResultFormName: result.formName || null,
        HtmlContent: finalHtml || null,
        MessageIndex: nextIndex + 1
      };

      if (eFormDefinitionId) {
        try {
          await saveChatMessages(userItem, assistantItem);
        } catch (e) {
          console.warn('Save chat failed', e);
        }
      }

      setMessages(prev => [
        ...prev,
        { ...userItem, Id: null, CreatedAt: new Date().toISOString() },
        { ...assistantItem, Id: null, HtmlContent: finalHtml, CreatedAt: new Date().toISOString() }
      ]);
    } catch (error) {
      console.error('AI generate error', error);
      if (error.name === 'AbortError') {
        message.error(t('eformDesigner.requestTimeoutAiGeneration'));
      } else {
        message.error(`${t('eformDesigner.generationFailed')}${error.message}`);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUndoToVersion = (msg) => {
    const html = msg.htmlContent || msg.HtmlContent;
    const name = msg.resultFormName || msg.ResultFormName;
    if (html && typeof onSuccess === 'function') {
      onSuccess(html, name || null);
      message.success(t('eformDesigner.restoredToThisVersion') || '已還原到此版本');
    }
  };

  const renderAttachmentHint = (msg) => {
    const raw = msg.attachmentInfo || msg.AttachmentInfo;
    if (!raw) return null;
    try {
      const info = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const parts = [];
      if (info.hasImage) parts.push(t('eformDesigner.uploadedImage') || '已上傳圖片');
      if (info.hasDocument && info.documentName) parts.push(info.documentName);
      if (parts.length === 0) return null;
      return <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>{parts.join(' · ')}</div>;
    } catch {
      return null;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 320, height: '100%' }}>
      {/* 對話區：maxHeight 用 vh 確保有固定上限，內容超出時顯示捲軸 */}
      <div
        style={{
          flex: '1 1 0%',
          minHeight: 120,
          maxHeight: '60vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          border: '1px solid #e8e8e8',
          borderRadius: 8,
          padding: 8,
          marginBottom: 8,
          backgroundColor: '#fafafa'
        }}
      >
        {loadingHistory && (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Spin size="small" />
          </div>
        )}
        {!loadingHistory && messages.length === 0 && (
          <div style={{ textAlign: 'center', color: '#999', fontSize: 12, padding: 16 }}>
            {t('eformDesigner.chatEmptyHint') || '輸入需求或上傳圖片/文件，AI 將協助生成或修改表單'}
          </div>
        )}
        {!loadingHistory && messages.map((msg) => {
          const role = msg.role || msg.Role;
          const content = msg.content || msg.Content;
          const hasHtml = !!(msg.htmlContent || msg.HtmlContent);
          return (
          <div
            key={msg.id || `${msg.MessageIndex}-${role}`}
            style={{
              display: 'flex',
              justifyContent: role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 8
            }}
          >
            <div
              style={{
                maxWidth: '92%',
                padding: '8px 12px',
                borderRadius: 12,
                backgroundColor: role === 'user' ? '#1890ff' : '#f0f0f0',
                color: role === 'user' ? '#fff' : '#333',
                fontSize: 13
              }}
            >
              {role === 'assistant' ? (
                <div className="eform-ai-chat-markdown" style={markdownChatStyles}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents(false)}>
                    {content || ''}
                  </ReactMarkdown>
                </div>
              ) : (
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{content}</div>
              )}
              {renderAttachmentHint(msg)}
              {role === 'assistant' && hasHtml && (
                <Button
                  type="link"
                  size="small"
                  icon={<RollbackOutlined />}
                  onClick={() => handleUndoToVersion(msg)}
                  style={{ padding: 0, marginTop: 6, color: role === 'user' ? '#fff' : '#1890ff' }}
                >
                  {t('eformDesigner.restoreToThisVersion') || '還原到此版本'}
                </Button>
              )}
            </div>
          </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* 未儲存表單時提示：對話僅在儲存後寫入 */}
      {!eFormDefinitionId && (
        <div style={{ fontSize: 11, color: '#999', marginBottom: 6 }}>
          {t('eformDesigner.chatSaveHintNewForm') || '儲存表單後，對話紀錄將寫入'}
        </div>
      )}

      {/* 輸入區 */}
      <div style={{ flexShrink: 0 }}>
        <div style={{ marginBottom: 6 }}>
          {!uploadedImage ? (
            <Upload
              accept="image/*"
              beforeUpload={handleImageUpload}
              showUploadList={false}
              disabled={isGenerating}
            >
              <Button size="small" icon={<FileImageOutlined />} disabled={isGenerating}>
                {t('eformDesigner.uploadImage') || '圖片'}
              </Button>
            </Upload>
          ) : (
            <>
              <span style={{ fontSize: 12, marginRight: 4 }}>{uploadedImage.name}</span>
              <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={handleRemoveImage} disabled={isGenerating} />
            </>
          )}
          {' '}
          {!uploadedDocument ? (
            <Upload accept={DOC_ACCEPT} beforeUpload={handleDocumentUpload} showUploadList={false} disabled={isGenerating}>
              <Button size="small" icon={<FileWordOutlined />} disabled={isGenerating}>
                {t('eformDesigner.uploadDoc') || 'Word/PDF'}
              </Button>
            </Upload>
          ) : (
            <>
              <span style={{ fontSize: 12 }}>{uploadedDocument.name}</span>
              <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={handleRemoveDocument} disabled={isGenerating} />
            </>
          )}
        </div>
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <TextArea
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder={t('eformDesigner.placeholderExample') || '例如：創建包含姓名、電話的表單'}
            rows={4}
            style={{ paddingRight: 40, fontSize: 13 }}
            disabled={isGenerating}
          />
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={isGenerating}
            disabled={
              (!aiPrompt.trim() && !uploadedImage && !uploadedDocument) || (aiProviders.length > 0 && !selectedProviderKey)
            }
            style={{
              position: 'absolute',
              right: 6,
              bottom: 6,
              minWidth: 32,
              height: 28,
              padding: '0 8px'
            }}
            title={t('eformDesigner.generateForm')}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            {t('eformDesigner.formStyle') || '表單風格'}
          </label>
          <Segmented
            size="small"
            value={formStyle}
            onChange={handleFormStyleChange}
            disabled={isGenerating}
            block
            options={[
              { label: t('eformDesigner.formStyleUi') || '操作式 UI', value: 'ui' },
              { label: t('eformDesigner.formStylePrint') || '可打印文件', value: 'print' }
            ]}
          />
          {formStyle === 'print' && (
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
              {t('eformDesigner.formStylePrintHint') || '適合上傳圖片/PDF/Word，盡量 1:1 還原原始文件版面'}
            </div>
          )}
        </div>
        {(htmlContent || '').trim() && (
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <Switch
              size="small"
              checked={includeCurrentHtml}
              onChange={setIncludeCurrentHtml}
              disabled={isGenerating}
              style={{ marginRight: 4 }}
            />
            {t('eformDesigner.includeCurrentHtml')}
          </label>
        )}

        <Collapse
          size="small"
          items={[
            {
              key: '1',
              label: t('eformDesigner.advancedOptions') || '進階選項',
              children: (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: 12 }}>{t('eformDesigner.aiProvider')}</label>
                    <Select
                      value={selectedProviderKey || undefined}
                      onChange={setSelectedProviderKey}
                      placeholder={t('eformDesigner.aiProviderPlaceholder')}
                      style={{ width: '100%' }}
                      size="small"
                      loading={loadingProviders}
                      allowClear
                      options={aiProviders.map(p => ({
                        value: p.providerKey,
                        label: `${p.displayName}${p.active ? '' : ` (${t('workflowDesigner.aiProviderInactive')})`}`,
                        disabled: p.active === false
                      }))}
                    />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
                      <Switch size="small" checked={sendDefaultSystemPrompt} onChange={handleSendDefaultSystemPromptChange} style={{ marginRight: 8 }} disabled={isGenerating} />
                      {t('eformDesigner.sendDefaultSystemPrompt')}
                    </label>
                    {sendDefaultSystemPrompt && (
                      <label style={{ display: 'flex', alignItems: 'center', marginTop: 4, fontSize: 12, opacity: 0.9 }}>
                        <input
                          type="checkbox"
                          checked={modifySystemPrompt}
                          onChange={(e) => handleModifySystemPromptChange(e.target.checked)}
                          style={{ marginRight: 8 }}
                          disabled={isGenerating}
                        />
                        {t('eformDesigner.modifySystemPrompt')}
                      </label>
                    )}
                    {sendDefaultSystemPrompt && modifySystemPrompt && (
                      <TextArea
                        value={customSystemPrompt}
                        onChange={(e) => setCustomSystemPrompt(e.target.value)}
                        placeholder={loadingDefaultPrompt ? (t('eformDesigner.loadingDefaultPrompt') || '載入預設中...') : (t('eformDesigner.systemPromptPlaceholder') || '系統 prompt')}
                        rows={4}
                        style={{ fontSize: 12, fontFamily: 'monospace', marginTop: 6 }}
                        disabled={isGenerating || loadingDefaultPrompt}
                      />
                    )}
                  </div>
                </>
              )
            }
          ]}
        />
      </div>
    </div>
  );
};

export default EFormDesignerAIChat;
